import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';

import 'ag-grid-enterprise';

import {
  getAntibodies,
  getAntibody,
  getMultiplexAntibodies,
  getPanels,
  getSpecialStainsIHC,
} from 'services/resources/commonResources';
import { useUnmountIgnore } from 'utilities/useUnmountIgnore';
import { appURL, backendURL } from 'services/backendAPI';
import { Button, Collapse, Link, Stack, Typography } from '@mui/material';
import {
  AntibodyColumnDefs,
  ihcAdminColumnDefs,
} from 'components/utilities/AgGridCols/AgGridColumns';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { PATHOLOGY_MAP_SEARCH_2 } from 'constants/urls';
import Grid from '@mui/material/Grid';
import { TabPanel } from 'components/Shared/TabPanel';
import { FormInput } from 'components/FormInputs/FormInput';
import { SingleAntibodyTable } from 'components/AntibodiesList/SingleAntibodyTable';
import { MultiplexAntibodyTable } from 'components/AntibodiesList/MultiplexAntibodyTable';
import { IFPanelTable } from 'components/AntibodiesList/IFPanelTable';
import {
  DETAILS_VIEW_MODE,
  HEADER_TABS_PROPS,
  MULTIPLEX_ANTIBODY_TABLE_IDX,
  PANELS_TABLE_IDX,
  SINGLE_ANTIBODY_TABLE_IDX,
  SPECIAL_STAINS_TABLE_IDX,
  ORIGENE_AB_TABLE_IDX,
  VIEWER_VIEW_MODE,
  FRAGMENT_TO_TAB_IDX,
  TAB_IDX_TO_FRAGMENT,
} from 'components/AntibodiesList/constants';
import {
  FETCH_ANTIBODIES_ERROR_MESSAGE,
  FETCH_MULTIPLEX_ANTIBODIES_ERROR_MESSAGE,
  FETCH_SPECIAL_STAINS_ERROR_MESSAGE,
} from 'constants/errorMessages';
import { userDetailsSelector } from 'store/slices/userDetailsSlice';
import { useSelector } from 'react-redux';
import { SpecialStainsTable } from 'components/AntibodiesList/SpecialStainsTable';
import { DATE_REQUEST_FORMAT } from 'utilities/dates_and_times';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { AntibodySummary } from 'components/AntibodiesList/AntibodySummary';
import { DetailsHeader } from 'components/AntibodiesList/DetailsTabHeader';
import { SimpleSlideView } from 'components/ImageViewer/SimpleSlideImageView';
import { useRouteMatch, Link as RouterLink } from 'react-router-dom';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ShoppingCartOutlined as ShoppingCart,
} from '@mui/icons-material';
import { AntibodiesListLegend } from 'components/AntibodiesList/AntibodiesListLegend';
import { useAntibodiesListStyles } from 'components/AntibodiesList/antibodiesListStyles';
import {
  ANTIBODIES_CATALOG_CANONICAL_URL,
  ANTIBODIES_INDEX_PATH,
  getAntibodyCanonicalUrl,
  getAntibodyJsonLd,
  getAntibodySeoDescription,
  getAntibodySeoTitle,
  toJsonLdString,
} from 'components/AntibodiesList/antibodySeo';
import { useHistory, useLocation } from 'react-router';

// TODO: this component should be refactored - code is quite messy and hard to read, tons of ifs
export const AntibodiesListView = ({
  isForOrderForm,
  handleAddToOrder,
  isForSlide,
  handleAddToSlide,
}) => {
  const unmountIgnore = useUnmountIgnore();
  const userDetails = useSelector(userDetailsSelector);
  const { showError } = useSnackbar();
  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const [columnDefs, setColumnDefs] = useState(AntibodyColumnDefs);
  /* eslint-disable-next-line no-unused-vars */
  const [currentDataLength, setCurrentDataLength] = useState(0);

  const [antibodiesRowData, setAntibodiesRowData] = useState([]);
  const [isAntibodiesLoading, setIsAntibodiesLoading] = useState(true);
  const [hasAntibodiesLoaded, setHasAntibodiesLoaded] = useState(false);
  const [multiplexRowData, setMultiplexRowData] = useState([]);
  const [specialStains, setSpecialStains] = useState([]);
  const [isMultiplexAntibodiesLoading, setIsMultiplexAntibodiesLoading] =
    useState(true);
  const [isSpecialStainsLoading, setIsSpecialStainsLoading] = useState(true);
  const [panelsRowData, setPanelsRowData] = useState([]);
  const [isPanelsLoading, setIsPanelsLoading] = useState(true);
  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(false);
  // Everyone gets the spec table first and the zoomable viewer is one click away. A single
  // default for every client means no user-agent sniffing, and real text on the page for
  // visitors and crawlers alike.
  const [selectedViewMode, setSelectedViewMode] = useState(DETAILS_VIEW_MODE);

  const fragment = location.hash.replace(/^#/, '');
  const defaultCurrentTab =
    FRAGMENT_TO_TAB_IDX[fragment] ?? SINGLE_ANTIBODY_TABLE_IDX;

  const [currentTab, setCurrentTab] = useState(defaultCurrentTab);
  const [selectedAntibody, setSelectedAntibody] = useState(null);

  const [slide, setSlide] = useState();

  // Add state for OriGene filtered antibodies
  const [origeneAntibodiesRowData, setOrigeneAntibodiesRowData] = useState([]);
  const [isOrigeneAntibodiesLoading, setIsOrigeneAntibodiesLoading] =
    useState(true);

  const [currentGridApi, setCurrentGridApi] = useState(null);

  // Include both IHC and OriGene tabs as main tabs to show details panel
  const isMainTab =
    currentTab === SINGLE_ANTIBODY_TABLE_IDX ||
    currentTab === ORIGENE_AB_TABLE_IDX;

  const isDetailsView = selectedViewMode === DETAILS_VIEW_MODE;

  const { classes } = useAntibodiesListStyles({
    isFullTable: !slide && isMainTab,
    isDetailsCollapsed,
  });

  const [quickFilterText, setQuickFilterText] = useState('');

  const [isStaff, setIsStaff] = useState(false);
  const [singleAntibodyGridApi, setSingleAntibodyGridApi] = useState(null);
  const [, setSingleAntibodyDetailGridApi] = useState(null);
  const [origeneAntibodyGridApi, setOrigeneAntibodyGridApi] = useState(null);
  const [, setOrigeneAntibodyDetailGridApi] = useState(null);

  const antibodyUUIDFromUrl = match?.params['antibodyUUID'];

  // This component is also mounted inside the order form and the slide-metadata modal, so
  // per-page SEO tags belong only on the public /ihc-antibodies route.
  const isPublicCatalogRoute = !isForOrderForm && !isForSlide;
  const isAntibodyDeepLink = isPublicCatalogRoute && !!antibodyUUIDFromUrl;

  // On the catalog URL itself nothing is selected until the user picks an antibody: letting
  // the grid select one would also rewrite the URL to it, and the catalog is the page that
  // has to keep its own URL.
  const isCatalogBrowseRoute = isPublicCatalogRoute && !antibodyUUIDFromUrl;

  // "Not found" has to mean a lookup that succeeded and came back empty. A failed fetch
  // also leaves antibodiesRowData empty, and gating on the loading flag alone would put
  // noindex on a live page over one API blip — so require the catalog to have actually
  // arrived, and require the tab that renders the not-found state.
  const isAntibodyNotFound =
    isMainTab && isAntibodyDeepLink && hasAntibodiesLoaded && !selectedAntibody;

  // The catalog itself shows an antibody in its details panel, but it is not that
  // antibody's page: only a URL that names one gets antibody-level metadata.
  const seoAntibody =
    isAntibodyDeepLink && !isAntibodyNotFound ? selectedAntibody : null;

  // Keyed on the antibody, so the quick-filter box, tab switches and grid updates do not
  // rebuild the description and the structured data on every keystroke.
  const { seoTitle, seoDescription, jsonLd } = useMemo(() => {
    const description = getAntibodySeoDescription(seoAntibody);

    return {
      seoTitle: getAntibodySeoTitle(seoAntibody),
      seoDescription: description,
      jsonLd: getAntibodyJsonLd(seoAntibody, description),
    };
  }, [seoAntibody]);

  // A deep link gets no canonical until its antibody is known: falling back to the catalog
  // URL while it loads would have the antibody page declare itself a duplicate.
  const deepLinkCanonicalUrl = seoAntibody
    ? getAntibodyCanonicalUrl(seoAntibody)
    : null;
  const canonicalUrl = isAntibodyDeepLink
    ? deepLinkCanonicalUrl
    : ANTIBODIES_CATALOG_CANONICAL_URL;

  useEffect(() => {
    // set current grid API based on current tab
    const mapGridApiToTab = {
      [SINGLE_ANTIBODY_TABLE_IDX]: singleAntibodyGridApi,
      [ORIGENE_AB_TABLE_IDX]: origeneAntibodyGridApi,
    };

    const gridApi = mapGridApiToTab[currentTab] || null;

    setCurrentGridApi(gridApi);
  }, [
    singleAntibodyGridApi,
    origeneAntibodyGridApi,
    currentTab,
    setCurrentGridApi,
  ]);

  useEffect(() => {
    if (!(antibodiesRowData.length || origeneAntibodiesRowData.length)) return;

    const mapAntibodiesListToTab = {
      [SINGLE_ANTIBODY_TABLE_IDX]: antibodiesRowData,
      [ORIGENE_AB_TABLE_IDX]: origeneAntibodiesRowData,
    };

    const antibodiesRowDataForTab = mapAntibodiesListToTab[currentTab];

    // tab is not supported
    if (!antibodiesRowDataForTab) {
      return;
    }

    // A URL naming an antibody we do not have must not fall back to another one: every
    // unknown id would then serve a duplicate of the same page under a different URL.
    if (antibodyUUIDFromUrl) {
      const antibodyFromUrl = antibodiesRowData.find(
        ({ uuid }) => uuid === antibodyUUIDFromUrl,
      );

      setSelectedAntibody(antibodyFromUrl || null);
      setSlide(antibodyFromUrl?.validated_slides?.[0]);

      return;
    }

    const defaultAntibody = antibodiesRowDataForTab[0];

    setSelectedAntibody(defaultAntibody);
    setSlide(defaultAntibody?.validated_slides?.[0]);
  }, [
    antibodiesRowData,
    origeneAntibodiesRowData,
    antibodyUUIDFromUrl,
    currentTab,
  ]);

  // A deep-linked antibody is fetched on its own so the page has its title, description,
  // structured data and spec table without waiting for the whole 3.3 MB catalog. The list
  // fetch above is still the fallback — and the only source once the catalog is in, which
  // is why a row click does not hit this again.
  useEffect(() => {
    if (!isAntibodyDeepLink || antibodiesRowData.length) return undefined;

    let isCancelled = false;

    getAntibody(antibodyUUIDFromUrl)
      .then((antibody) => {
        if (isCancelled || !antibody) return;

        setSelectedAntibody(antibody);
        setSlide(antibody?.validated_slides?.[0]);
      })
      .catch(() => {
        // Expected until the detail endpoint is deployed: the catalog fetch still fills
        // this in, so a miss here is not an error worth surfacing.
      });

    return () => {
      isCancelled = true;
    };
  }, [isAntibodyDeepLink, antibodyUUIDFromUrl, antibodiesRowData.length]);

  useEffect(() => {
    if (singleAntibodyGridApi) {
      singleAntibodyGridApi.sizeColumnsToFit();
    }
    if (origeneAntibodyGridApi) {
      origeneAntibodyGridApi.sizeColumnsToFit();
    }
  }, [singleAntibodyGridApi, origeneAntibodyGridApi, isDetailsCollapsed]);

  useEffect(() => {
    getAntibodies()
      .then((response) => {
        if (!unmountIgnore.current) {
          setAntibodiesRowData(response);
          setHasAntibodiesLoaded(true);
          const origeneAntibodies = response.filter(
            (antibody) => antibody.manufacturer.toLowerCase() === 'origene',
          );
          setOrigeneAntibodiesRowData(origeneAntibodies);
        }
      })
      .catch(() => showError(FETCH_ANTIBODIES_ERROR_MESSAGE))
      .finally(() => {
        setIsOrigeneAntibodiesLoading(false);
        setIsAntibodiesLoading(false);
      });

    getPanels()
      .then((response) => {
        if (!unmountIgnore.current) {
          setPanelsRowData(response);
        }
      })
      .catch(() => showError(FETCH_MULTIPLEX_ANTIBODIES_ERROR_MESSAGE))
      .finally(() => setIsPanelsLoading(false));

    getMultiplexAntibodies()
      .then((response) => {
        if (!unmountIgnore.current) {
          setMultiplexRowData(response);
        }
      })
      .catch(() => showError(FETCH_MULTIPLEX_ANTIBODIES_ERROR_MESSAGE))
      .finally(() => setIsMultiplexAntibodiesLoading(false));

    getSpecialStainsIHC()
      .then((response) => {
        if (!unmountIgnore.current) {
          setSpecialStains(response);
        }
      })
      .catch(() => showError(FETCH_SPECIAL_STAINS_ERROR_MESSAGE))
      .finally(() => setIsSpecialStainsLoading(false));
  }, [unmountIgnore]);

  const handleExportToExcel = () => {
    const now = moment().format(DATE_REQUEST_FORMAT);
    const tabName = currentTab === ORIGENE_AB_TABLE_IDX ? 'OriGene' : 'Single';
    const fileName = `${tabName}-Antibodies-Catalog-${now}`;

    const exportParams = {
      columnGroups: true,
      fileName,
    };

    currentGridApi?.exportDataAsExcel(exportParams);
  };

  useEffect(() => {
    // ag-grid caches a some functions, which causes an issue because by default for context we say is not a staff
    if (userDetails.isStaff) {
      setColumnDefs(ihcAdminColumnDefs);
      setIsStaff(true);
    }
  }, [userDetails]);

  const getSingleContextMenuItems = ({ row }) => {
    const lisaLink = `${backendURL}/lisa/core/antibody/${row.id}/change/`;

    const antibodyName = row.name.toUpperCase();

    const pathologyMapSearchURL = `${appURL}${PATHOLOGY_MAP_SEARCH_2}?production_pathology_map[refinementList][antibody_name][0]=${antibodyName}`;

    return [
      {
        name: 'Edit Antibody in LISA',
        action: () => {
          if (row) {
            window.open(lisaLink, '_blank');
          }
        },
      },
      {
        name: 'Find Slides in PathologyMap',
        action: () => {
          if (row) {
            window.open(pathologyMapSearchURL, '_blank');
          }
        },
      },
    ];
  };

  const getContextMenuItems = (params) => {
    // this function gets cached by ag grid, which is a problem when we actually use
    // react context for is staff and that changes, so here, we pass it into ag-grid as a constantly
    // updating context
    const gridContext = params.context;
    const { isStaff: isUserStaff } = gridContext;

    if (!params.node?.data || !isUserStaff) {
      return null;
    }

    const row = params.node.data;

    return getSingleContextMenuItems({ row });
  };

  const onQuickFilterText = (event) => {
    setQuickFilterText(event.target.value);
  };

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
    setSlide(undefined);

    // Clear selection when switching away from main tabs
    if (value !== SINGLE_ANTIBODY_TABLE_IDX && value !== ORIGENE_AB_TABLE_IDX) {
      setSelectedAntibody(null);
    }

    if (TAB_IDX_TO_FRAGMENT[value]) {
      history.replace({ hash: `#${TAB_IDX_TO_FRAGMENT[value]}` });
    }
  };

  const setDisplayRowLengthHandler = (event) => {
    setCurrentDataLength(
      event.api.rowModel.rowsToDisplay.filter((element) => !element.detail)
        .length,
    );
  };

  const thumbnail = slide?.large_thumbnail_url || '';
  const isFullWidth = (isDetailsCollapsed && isMainTab) || !isMainTab;
  const mainGridWidth = isFullWidth ? 11.8 : 5.8;

  // Get selected rows from both IHC and ORIGENE tabs with safety checks

  // TODO: it's too many calls - needs some refactor
  const ihcSelectedRows = singleAntibodyGridApi?.getSelectedRows() || [];
  const origeneSelectedRows = origeneAntibodyGridApi?.getSelectedRows() || [];
  const selectedRows = currentGridApi?.getSelectedRows() || [];
  const selectedRowsCount = selectedRows.length;

  // Calculate total selections across both tabs for display
  const totalSelectionsCount =
    ihcSelectedRows.length + origeneSelectedRows.length;

  return (
    <>
      {isPublicCatalogRoute && (
        // defer={false} applies the tags synchronously: helmet's default defers to
        // requestAnimationFrame, which never runs in a hidden or non-compositing tab, so
        // the tags would be missing for exactly the clients that need them.
        <Helmet defer={false}>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          {!!canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
          {isAntibodyNotFound && <meta name="robots" content="noindex" />}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="HistoWiz" />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          {!!canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
          {!!thumbnail && <meta property="og:image" content={thumbnail} />}
          <meta
            name="twitter:card"
            content={thumbnail ? 'summary_large_image' : 'summary'}
          />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescription} />
          {!!jsonLd && (
            <script type="application/ld+json">{toJsonLdString(jsonLd)}</script>
          )}
        </Helmet>
      )}
      <Grid container className={classes.wrapper}>
        <Grid item xs={mainGridWidth} className={classes.gridWrapper}>
          <Box className={classes.tabsWrapper}>
            <Tabs
              value={currentTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleTabsChange}
              variant="scrollable"
              scrollButtons="auto"
              classes={{
                root: classes.tabs,
                indicator: classes.indicator,
              }}
            >
              {HEADER_TABS_PROPS.map(({ label, id }) => (
                <Tab
                  key={id + label}
                  label={label}
                  id={id}
                  classes={{
                    root: classes.tab,
                    selected: classes.selectedTab,
                  }}
                />
              ))}
            </Tabs>
            <AntibodiesListLegend />
          </Box>
          <Grid item>
            <Box
              ml={1}
              justifyContent="space-between"
              display="flex"
              alignItems="center"
            >
              <FormInput onChange={onQuickFilterText} />
              {isForOrderForm && selectedRowsCount > 1 && (
                <Button
                  variant="contained"
                  className={classes.addABToOrderButton}
                  startIcon={<ShoppingCart />}
                  onClick={() => handleAddToOrder(selectedRows)}
                >
                  Add
                  <Box
                    className={classes.addToOrderCounter}
                    key={selectedRowsCount}
                  >
                    {selectedRowsCount}
                  </Box>
                  {totalSelectionsCount > selectedRowsCount && (
                    <Box component="span" ml={0.5}>
                      (of {totalSelectionsCount})
                    </Box>
                  )}
                  Selected to Order
                </Button>
              )}
            </Box>

            <Box className={`ag-theme-balham ${classes.agGrid}`}>
              <TabPanel value={currentTab} index={SINGLE_ANTIBODY_TABLE_IDX}>
                <SingleAntibodyTable
                  setDisplayRowData={setDisplayRowLengthHandler}
                  rowData={antibodiesRowData}
                  isLoading={isAntibodiesLoading}
                  quickFilterText={quickFilterText}
                  columnDefs={columnDefs}
                  getContextMenuItems={getContextMenuItems}
                  isStaff={isStaff}
                  setSlide={setSlide}
                  setGridApi={setSingleAntibodyGridApi}
                  gridApi={singleAntibodyGridApi}
                  setDetailGridApi={setSingleAntibodyDetailGridApi}
                  selectedAntibody={selectedAntibody}
                  setSelectedAntibody={setSelectedAntibody}
                  isForOrderForm={isForOrderForm}
                  isForSlide={isForSlide}
                  hasUnresolvedDeepLink={isAntibodyNotFound}
                  skipAutoSelect={isCatalogBrowseRoute}
                />
                {isStaff && (
                  <Stack direction="row" justifyContent="end">
                    <Button onClick={handleExportToExcel}>
                      Export to Excel
                    </Button>
                  </Stack>
                )}
              </TabPanel>
              <TabPanel value={currentTab} index={MULTIPLEX_ANTIBODY_TABLE_IDX}>
                <MultiplexAntibodyTable
                  setDisplayRowData={setDisplayRowLengthHandler}
                  rowData={multiplexRowData}
                  isLoading={isMultiplexAntibodiesLoading}
                  quickFilterText={quickFilterText}
                  getContextMenuItems={getContextMenuItems}
                  isStaff={isStaff}
                />
              </TabPanel>
              <TabPanel value={currentTab} index={PANELS_TABLE_IDX}>
                <IFPanelTable
                  rowData={panelsRowData}
                  isLoading={isPanelsLoading}
                  quickFilterText={quickFilterText}
                  isStaff={isStaff}
                  setDisplayRowData={setDisplayRowLengthHandler}
                />
              </TabPanel>
              <TabPanel value={currentTab} index={SPECIAL_STAINS_TABLE_IDX}>
                <SpecialStainsTable
                  rowData={specialStains}
                  isLoading={isSpecialStainsLoading}
                  quickFilterText={quickFilterText}
                  setDisplayRowData={setDisplayRowLengthHandler}
                />
              </TabPanel>
              <TabPanel value={currentTab} index={ORIGENE_AB_TABLE_IDX}>
                <SingleAntibodyTable
                  setDisplayRowData={setDisplayRowLengthHandler}
                  rowData={origeneAntibodiesRowData}
                  isLoading={isOrigeneAntibodiesLoading}
                  quickFilterText={quickFilterText}
                  columnDefs={columnDefs}
                  getContextMenuItems={getContextMenuItems}
                  isStaff={isStaff}
                  setSlide={setSlide}
                  setGridApi={setOrigeneAntibodyGridApi}
                  gridApi={origeneAntibodyGridApi}
                  setDetailGridApi={setOrigeneAntibodyDetailGridApi}
                  selectedAntibody={selectedAntibody}
                  setSelectedAntibody={setSelectedAntibody}
                  isForOrderForm={isForOrderForm}
                  isForSlide={isForSlide}
                  hasUnresolvedDeepLink={isAntibodyNotFound}
                  skipAutoSelect={isCatalogBrowseRoute}
                />
                {isStaff && (
                  <Stack direction="row" justifyContent="end">
                    <Button onClick={handleExportToExcel}>
                      Export to Excel
                    </Button>
                  </Stack>
                )}
              </TabPanel>
            </Box>
          </Grid>
        </Grid>
        {isMainTab && (
          <Grid item xs={0.2}>
            <Button
              className={classes.collapseButton}
              onClick={() => setIsDetailsCollapsed(!isDetailsCollapsed)}
            >
              {isDetailsCollapsed ? (
                <KeyboardArrowLeft />
              ) : (
                <KeyboardArrowRight />
              )}
            </Button>
          </Grid>
        )}
        {isMainTab && (
          <Grid item xs={6} className={classes.detailsWrapper}>
            <Collapse
              orientation="horizontal"
              in={!isDetailsCollapsed}
              className={classes.collapseContainer}
            >
              <Box className={classes.detailsCollapsibleContainer}>
                {!isDetailsCollapsed && (
                  <DetailsHeader
                    selectedAntibody={selectedAntibody}
                    selectedViewMode={selectedViewMode}
                    setSelectedViewMode={setSelectedViewMode}
                    thumbnail={thumbnail}
                    isAntibodyDeepLink={isAntibodyDeepLink}
                    isPublicCatalog={isPublicCatalogRoute}
                  />
                )}
                <Grid
                  item
                  className={`${!isDetailsView && classes.viewerContainer}`}
                >
                  {selectedAntibody &&
                    (isDetailsView ? (
                      <AntibodySummary
                        selectedAntibody={selectedAntibody}
                        thumbnail={thumbnail}
                        classes={classes}
                        handleThumbnail={() =>
                          setSelectedViewMode(VIEWER_VIEW_MODE)
                        }
                        isFromOrderForm={isForOrderForm}
                        handleAddToOrder={handleAddToOrder}
                        isForSlide={isForSlide}
                        handleAddToSlide={handleAddToSlide}
                        selectedRowsCount={selectedRowsCount}
                      />
                    ) : (
                      slide && (
                        <SimpleSlideView
                          slide={slide}
                          id={slide?.uuid}
                          showNavigator={false}
                        />
                      )
                    ))}
                  {isAntibodyNotFound && (
                    <Box mt={3}>
                      <Typography variant="h6" gutterBottom>
                        Antibody not found
                      </Typography>
                      <Typography>
                        No antibody matches the id{' '}
                        <code>{antibodyUUIDFromUrl}</code>.{' '}
                        <Link component={RouterLink} to={ANTIBODIES_INDEX_PATH}>
                          Browse all IHC antibodies
                        </Link>
                        .
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </Grid>
        )}
      </Grid>
    </>
  );
};
